/**
 * PMS ekranlarının metinleri (M7a; ADR 0029). Ekran ve alan adları tasarımdaki gibi İngilizce
 * (`gemi-pms-ekipman.jpeg`); Türkçe ikinci dil. Hata kodları domain'deki `PMS_ERROR_CODES`
 * listesini karşılar [NR496 4.1.5] (`locales.test.ts`).
 */

import {
  pmsConditionEn,
  pmsConditionErrorsEn,
  pmsConditionErrorsTr,
  pmsConditionTr,
} from './pms-condition.js';
import { pmsPostponementsEn, pmsPostponementsTr } from './pms-postponements.js';
import { pmsSetupChangesEn, pmsSetupChangesTr } from './pms-setup-changes.js';
import {
  pmsWorkEn,
  pmsWorkErrorsEn,
  pmsWorkErrorsTr,
  pmsWorkFieldErrorsEn,
  pmsWorkFieldErrorsTr,
  pmsWorkTr,
} from './pms-work.js';

const errorsEn = {
  GENERIC: 'The operation failed. Try again.',
  NETWORK: 'No connection. Check the network and try again.',
  EQUIPMENT_CYCLE: 'An item cannot be moved under itself or one of its own components.',
  VESSEL_MISMATCH: 'The parent belongs to another vessel.',
  GROUP_NOT_AT_ROOT: 'A group can only be at the top level of the tree.',
  DUPLICATE_EQUIPMENT_CODE: 'Another item on this vessel already uses this code.',
  DUPLICATE_EQUIPMENT_TAG: 'Another item on this vessel already uses this equipment tag.',
  EQUIPMENT_HAS_CHILDREN: 'Remove or move its components first.',
  DECOMMISSION_REASON_REQUIRED: 'Write why the item is removed from the register.',
  EQUIPMENT_PARENT_DELETED: 'The parent item has been removed from the register.',
  INVALID_EQUIPMENT: 'Some fields are invalid.',
  INVALID_KIND: 'Unknown reading type.',
  INVALID_HOURS: 'Enter hours with at most one decimal place.',
  INVALID_DATE: 'Enter a valid date and time.',
  FUTURE_DATE: 'The date cannot be in the future (ship time).',
  NEGATIVE_VALUE: 'The value cannot be negative.',
  NOT_AFTER_PREVIOUS_READING: 'The reading time must be after the previous reading.',
  PREVIOUS_METER_FINAL_REQUIRED: 'Enter the final value of the old meter.',
  METER_START_REQUIRED: 'Enter the start value of the new meter.',
  METER_START_MISMATCH: 'The meter value must equal the start value of the new meter.',
  METER_CHANGE_REASON_REQUIRED: 'Write why the meter was replaced or reset.',
  CONFIRMATION_REQUIRED: 'The reading needs your explicit confirmation.',
  CUMULATIVE_MISMATCH: 'The cumulative total does not match the history. Reload and try again.',
  COUNTER_NOT_ENABLED: 'This item has no running hour counter.',
  INVALID_ROTABLE: 'Check the rotable component fields.',
  INVALID_ROTABLE_MOVEMENT: 'Check the install / remove fields.',
  ROTABLE_SERIAL_TAKEN:
    'A rotable component with this serial number is already registered on this vessel.',
  ROTABLE_DELETED: 'The rotable component has been removed from the register.',
  ROTABLE_INSTALLED: 'Remove the component from its position before taking it out of the register.',
  ROTABLE_ALREADY_INSTALLED: 'The component is already installed; remove it first.',
  ROTABLE_NOT_INSTALLED: 'The component is not installed.',
  ROTABLE_POSITION_MISMATCH: 'The component is installed at another position.',
  ROTABLE_DATE_ORDER: 'The date cannot be before the registration or the last movement.',
  ROTABLE_HOURS_DECREASE: 'The position hours cannot be less than at installation.',
  EQUIPMENT_DELETED: 'The item has been removed from the register.',
  INVALID_READING_SOURCE: 'The Noon Report source of the reading is not valid.',
  SUPERSEDES_MISMATCH: 'The reading it corrects belongs to another item.',
  INITIAL_PULL_PENDING: 'Wait for the first synchronisation with the office to finish.',
  EQUIPMENT_EXISTS: 'The vessel already has equipment; the default structure is not loaded.',
  EQUIPMENT_PENDING:
    'Equipment sent to the vessel is waiting for its next sync; the default structure is not loaded.',
  SHIP_IS_WRITER:
    'This change is made on board; the office changes the register through setup changes.',
  OFFICE_IS_WRITER: 'This record is maintained by the office.',
  ROW_VERSION_CONFLICT: 'Someone else changed this record. Reopen it and try again.',
  INVALID_VESSEL_PARTICULARS: 'Some vessel particulars are invalid.',
  NOT_FOUND: 'The record was not found. It may have been removed.',
  FORBIDDEN: 'You are not allowed to do this.',
  VALIDATION_FAILED: 'Some fields are invalid.',
};

const errorsTr: typeof errorsEn = {
  GENERIC: 'İşlem yapılamadı. Yeniden deneyin.',
  NETWORK: 'Bağlantı yok. Ağı denetleyip yeniden deneyin.',
  EQUIPMENT_CYCLE: 'Bir öğe kendisinin ya da kendi bileşeninin altına taşınamaz.',
  VESSEL_MISMATCH: 'Üst öğe başka bir gemiye ait.',
  GROUP_NOT_AT_ROOT: 'Grup yalnız ağacın en üst düzeyinde olabilir.',
  DUPLICATE_EQUIPMENT_CODE: 'Bu gemide aynı kodu kullanan başka bir öğe var.',
  DUPLICATE_EQUIPMENT_TAG: 'Bu gemide aynı ekipman etiketini kullanan başka bir öğe var.',
  EQUIPMENT_HAS_CHILDREN: 'Önce bileşenlerini kaldırın ya da taşıyın.',
  DECOMMISSION_REASON_REQUIRED: 'Öğenin kayıttan neden kaldırıldığını yazın.',
  EQUIPMENT_PARENT_DELETED: 'Üst öğe kayıttan kaldırılmış.',
  INVALID_EQUIPMENT: 'Bazı alanlar geçersiz.',
  INVALID_KIND: 'Bilinmeyen okuma türü.',
  INVALID_HOURS: 'Saati en çok bir ondalık basamakla girin.',
  INVALID_DATE: 'Geçerli bir tarih ve saat girin.',
  FUTURE_DATE: 'Tarih gelecekte olamaz (gemi saati).',
  NEGATIVE_VALUE: 'Değer eksi olamaz.',
  NOT_AFTER_PREVIOUS_READING: 'Okuma zamanı bir önceki okumadan sonra olmalı.',
  PREVIOUS_METER_FINAL_REQUIRED: 'Eski sayacın son değerini girin.',
  METER_START_REQUIRED: 'Yeni sayacın başlangıç değerini girin.',
  METER_START_MISMATCH: 'Sayaç değeri yeni sayacın başlangıç değerine eşit olmalı.',
  METER_CHANGE_REASON_REQUIRED: 'Sayacın neden değiştirildiğini ya da sıfırlandığını yazın.',
  CONFIRMATION_REQUIRED: 'Okuma sizin açık onayınızı gerektiriyor.',
  CUMULATIVE_MISMATCH: 'Birikmiş toplam geçmişle uyuşmuyor. Yenileyip yeniden deneyin.',
  COUNTER_NOT_ENABLED: 'Bu öğenin çalışma saati sayacı yok.',
  INVALID_ROTABLE: 'Dolaşan bileşenin alanlarını denetleyin.',
  INVALID_ROTABLE_MOVEMENT: 'Takma / sökme alanlarını denetleyin.',
  ROTABLE_SERIAL_TAKEN: 'Bu seri numaralı dolaşan bileşen bu gemide zaten kayıtlı.',
  ROTABLE_DELETED: 'Dolaşan bileşen kayıttan kaldırılmış.',
  ROTABLE_INSTALLED: 'Kayıttan kaldırmadan önce bileşeni konumundan sökün.',
  ROTABLE_ALREADY_INSTALLED: 'Bileşen zaten takılı; önce sökün.',
  ROTABLE_NOT_INSTALLED: 'Bileşen takılı değil.',
  ROTABLE_POSITION_MISMATCH: 'Bileşen başka bir konumda takılı.',
  ROTABLE_DATE_ORDER: 'Tarih kayıttan ya da son hareketten önce olamaz.',
  ROTABLE_HOURS_DECREASE: 'Konum saati takıldığı andakinden az olamaz.',
  EQUIPMENT_DELETED: 'Öğe kayıttan kaldırılmış.',
  INVALID_READING_SOURCE: 'Okumanın Noon Report kaynağı geçerli değil.',
  SUPERSEDES_MISMATCH: 'Düzelttiği okuma başka bir öğeye ait.',
  INITIAL_PULL_PENDING: 'Ofisle ilk senkronun bitmesini bekleyin.',
  EQUIPMENT_EXISTS: 'Gemide zaten ekipman var; varsayılan yapı yüklenmez.',
  EQUIPMENT_PENDING:
    'Gemiye gönderilen ekipman sonraki senkronu bekliyor; varsayılan yapı yüklenmez.',
  SHIP_IS_WRITER: 'Bu değişiklik gemide yapılır; ofis kaydı kurulum değişikliğiyle değiştirir.',
  OFFICE_IS_WRITER: 'Bu kaydı ofis tutar.',
  ROW_VERSION_CONFLICT: 'Kaydı başka biri değiştirdi. Yeniden açıp deneyin.',
  INVALID_VESSEL_PARTICULARS: 'Bazı gemi bilgileri geçersiz.',
  NOT_FOUND: 'Kayıt bulunamadı. Kaldırılmış olabilir.',
  FORBIDDEN: 'Bu işlem için yetkiniz yok.',
  VALIDATION_FAILED: 'Bazı alanlar geçersiz.',
};

/** Alan hataları: domain şemasının (`equipmentInputSchema`) ve formların iletileri. */
const fieldErrorsEn = {
  REQUIRED: 'Required.',
  TOO_LONG: 'Too long.',
  INVALID_TEXT: 'Enter text.',
  INVALID_OPTION: 'Choose an option.',
  INVALID_NUMBER: 'Enter a number.',
  OUT_OF_RANGE: 'The value is out of range.',
  INVALID_DATE: 'Enter a valid date.',
  FUTURE_DATE: 'The date cannot be in the future.',
  INVALID_HOURS: 'Enter hours with at most one decimal place.',
  NEGATIVE_VALUE: 'The value cannot be negative.',
  OUT_OF_PMS_REASON_REQUIRED: 'Write why the item is out of the PMS.',
  MAX_DAILY_OVER_24: 'A day has at most 24 hours.',
  INVALID_NOON_SOURCE: 'Choose the main engine or name an auxiliary engine.',
  NOON_SOURCE_REQUIRES_COUNTER: 'Turn on the running hour counter first.',
  INVALID_DECIMAL: 'Enter a number with a dot as the decimal separator.',
};

const fieldErrorsTr: typeof fieldErrorsEn = {
  REQUIRED: 'Zorunlu.',
  TOO_LONG: 'Çok uzun.',
  INVALID_TEXT: 'Metin girin.',
  INVALID_OPTION: 'Bir seçenek seçin.',
  INVALID_NUMBER: 'Sayı girin.',
  OUT_OF_RANGE: 'Değer aralık dışında.',
  INVALID_DATE: 'Geçerli bir tarih girin.',
  FUTURE_DATE: 'Tarih gelecekte olamaz.',
  INVALID_HOURS: 'Saati en çok bir ondalık basamakla girin.',
  NEGATIVE_VALUE: 'Değer eksi olamaz.',
  OUT_OF_PMS_REASON_REQUIRED: 'Öğenin neden PMS dışında olduğunu yazın.',
  MAX_DAILY_OVER_24: 'Bir gün en çok 24 saattir.',
  INVALID_NOON_SOURCE: 'Ana makineyi seçin ya da bir yardımcı makine adı yazın.',
  NOON_SOURCE_REQUIRES_COUNTER: 'Önce çalışma saati sayacını açın.',
  INVALID_DECIMAL: 'Sayıyı ondalık ayırıcı olarak nokta ile girin.',
};

export const pmsEn = {
  errors: { ...errorsEn, ...pmsWorkErrorsEn, ...pmsConditionErrorsEn },
  fieldErrors: { ...fieldErrorsEn, ...pmsWorkFieldErrorsEn },
  /** Kurulumdaki etkin gemi (ADR 0142 §3): ofisin değişikliği gemiye gider. */
  setupChanges: {
    title: 'Setup stage',
    text: 'The office sets up this vessel. Changes are sent to the vessel and applied at its next sync; the register here updates after that.',
    titleLive: 'Changes from the office',
    textLive:
      'The vessel is live. Changes are sent to the vessel and applied at its next sync; the register here updates after that. A record changed on board in the meantime is not overwritten: the change is listed as not applied.',
    pending_one: '{{count}} change waiting for the vessel',
    pending_other: '{{count}} changes waiting for the vessel',
    rejected_one: '{{count}} change could not be applied on board',
    rejected_other: '{{count}} changes could not be applied on board',
  },
  /** Dolaşan bileşenler (DNV-CP-0206 4.3 c; ADR 0133 §6). */
  rotables: {
    tab: 'Rotables',
    emptyHere: 'No rotable component installed here',
    installedHere: 'Rotable components installed here',
    installedSince: 'Installed {{day}}',
    totalHours: 'Own hours {{hours}}',
    initialHours: 'at registration {{hours}}',
    incomplete: 'incomplete (position without counter)',
    history: 'History',
    periods: 'Installation periods',
    now: 'now',
    install: 'Install rotable',
    remove: 'Remove',
    register: 'Register rotable',
    movementsHere: 'Movements at this position',
    kinds: { install: 'Installed', remove: 'Removed' },
    part: 'Rotable component',
    movedOn: 'Date',
    positionHours: 'Position running hours',
    positionHoursHint:
      'Counter total of this position at the time; leave empty if it has no counter.',
    reason: 'Reason / remarks',
    serialNo: 'Serial No.',
    name: 'Name',
    partNo: 'Part No.',
    maker: 'Maker',
    notes: 'Notes',
    initialHoursLabel: 'Hours at registration',
    initialHoursHint: 'Hours already run (e.g. since last overhaul); 0 if new.',
    registeredOn: 'Registered on',
  },
  work: pmsWorkEn,
  postponements: pmsPostponementsEn,
  setupChangeList: pmsSetupChangesEn,
  condition: pmsConditionEn,
  /** [NR496 4.5.1c, 4.5.1d] onay penceresinde tek tek (K5). */
  confirmations: {
    LOWER_THAN_PREVIOUS:
      'The value is lower than the previous reading ({{previous}} h). The meter was not replaced or reset.',
    DAILY_AVERAGE_OVER_24H:
      'Since the previous reading the item ran more than 24 hours a day ({{perDay}} h/day).',
    DAILY_AVERAGE_OVER_LIMIT:
      'Since the previous reading the item ran more than its daily limit of {{limit}} h ({{perDay}} h/day).',
  },
  /**
   * Sayaç değişiminde aynı onaylar (M7A-REG-3): eski sayacın son değeri karşılaştırılır; metin
   * değişimi inkâr etmez.
   */
  meterChangeConfirmations: {
    LOWER_THAN_PREVIOUS:
      "The old meter's final value ({{value}} h) is lower than the previous reading ({{previous}} h).",
    DAILY_AVERAGE_OVER_24H:
      "Up to the old meter's final value ({{value}} h) the item ran more than 24 hours a day since the previous reading ({{perDay}} h/day).",
    DAILY_AVERAGE_OVER_LIMIT:
      "Up to the old meter's final value ({{value}} h) the item ran more than its daily limit of {{limit}} h since the previous reading ({{perDay}} h/day).",
  },
  nodeTypes: {
    group: 'Group',
    system: 'System',
    equipment: 'Equipment',
    component: 'Component',
  },
  statuses: {
    operational: 'Running',
    operational_with_restrictions: 'Restricted',
    not_operational: 'Not Running',
  },
  surveyPrinciples: {
    pms_ce: 'PMS (Chief Engineer)',
    surveyor: 'Surveyor',
    condition_monitoring: 'Condition monitoring',
    none: 'None',
  },
  readingKinds: {
    reading: 'Reading',
    meter_replaced: 'Meter replaced',
    meter_reset: 'Meter reset',
  },
  readingSources: {
    manual: 'Manual',
    noon_report: 'Noon Report',
  },
  hours: '{{value}} h',
  equipment: {
    title: 'Equipment List',
    loading: 'Loading equipment…',
    error: 'The equipment register could not be loaded.',
    retry: 'Retry',
    noVessel: 'No vessel is assigned to you.',
    tableOf: '{{name}} table',
    card: {
      label: 'Vessel',
      vessel: 'Vessel',
      imo: 'IMO {{imo}}',
      vesselType: 'Vessel Type',
      built: 'Built',
      flag: 'Flag',
      nextPort: 'Next Port',
      search: 'Search equipment, component...',
      searchLabel: 'Search equipment',
      add: 'Add Equipment',
      export: 'Export',
      exportHint: 'Export arrives with the PMS reports.',
      noResults: 'No equipment found',
    },
    tree: {
      title: 'Equipment Tree',
      label: 'Equipment tree',
      search: 'Search...',
      searchLabel: 'Search the equipment tree',
      open: 'Equipment Tree',
      empty: 'No equipment yet',
      emptyShip: 'Add the first item or load the default structure.',
      emptyWeb: 'The vessel has not recorded equipment yet.',
      emptyQueued:
        'Build the register from the office: add equipment, apply items from the Component Library or load the default structure. The vessel applies the changes at its next sync.',
      emptyCloud: 'Prepare the register before the vessel goes live.',
      loadDefault: 'Load default structure',
      noMatch: 'No equipment matches "{{query}}".',
      critical: 'Critical',
      counter: 'Running hour counter',
    },
    overview: {
      title: '{{name}} – Equipment Overview',
      titleNone: 'Equipment Overview',
      totalComponents: 'Total Components',
      pmDue: 'PM Tasks (Due)',
      overdue: 'Overdue',
      completed: 'Completed (30 Days)',
      columns: {
        component: 'Component',
        type: 'Type',
        workType: 'Work Type',
        frequency: 'Frequency',
        lastDone: 'Last Done',
        nextDue: 'Next Due',
        status: 'Status',
        actions: 'Actions',
      },
      empty: 'No maintenance jobs yet',
      emptyHint: 'Jobs for this item are defined in the Maintenance Plan.',
      selectHint: 'Select an item in the tree.',
    },
    details: {
      title: 'Component Details',
      tabs: {
        general: 'General',
        plan: 'Maintenance Plan',
        /** Dar kartta görünen ad; erişilebilir ad tam addır (WCAG 2.5.3). */
        planShort: 'Plan',
        history: 'History',
        documents: 'Documents',
        rotables: 'Rotables',
      },
      equipment: 'Equipment',
      component: 'Component',
      workType: 'Work Type',
      frequency: 'Frequency',
      responsible: 'Responsible',
      location: 'Location',
      tagShort: 'Tag',
      notes: 'Notes',
      runningHours: 'Running Hours',
      lastReading: 'Last Reading',
      lastReadingValue: '{{hours}} on {{date}}',
      code: 'Code',
      maker: 'Maker / Model',
      serialNo: 'Serial No',
      critical: 'Critical',
      criticalInherited: 'Critical via {{name}}',
      classRelevant: 'Class',
      edit: 'Edit',
      viewManual: 'View Manual',
      viewManualHint: 'No manual is linked to this item yet.',
      enterHours: 'Enter Running Hours',
      decommission: 'Remove from register',
      editedOnBoard: 'Edited on board',
      editedOnBoardHint: 'The vessel is live: its equipment register is maintained on board.',
      preparedInOffice: 'Prepared in the office',
      noSelection: 'Select an item to see its details.',
      planEmpty: 'No maintenance jobs yet',
      nextDue: 'Next due {{date}}',
      workRecords: 'Work records',
      instructionNo: 'Instruction No {{no}}',
      historyEmpty: 'No running hour readings yet',
      auditTrail: 'Change history (audit trail)',
      historyColumns: {
        date: 'Date',
        kind: 'Type',
        hours: 'Meter',
        cumulative: 'Cumulative',
        by: 'Recorded by',
      },
      historyShowing: 'Showing {{from}}–{{to}} of {{total}} readings',
      historyPages: 'History pages',
      historySort: 'Sort by date',
      historyPage: {
        first: 'First page',
        previous: 'Previous page',
        next: 'Next page',
        last: 'Last page',
      },
      documentsEmpty: 'No manuals, instructions or attachments yet',
      photo: '{{type}} icon',
    },
    plan: {
      title: 'Maintenance Plan',
      month: 'Month',
      week: 'Week',
      list: 'List',
      today: 'Today',
      previous: 'Previous month',
      next: 'Next month',
      previousWeek: 'Previous week',
      nextWeek: 'Next week',
      period: 'Period',
      columns: {
        date: 'Date',
        component: 'Component',
        workType: 'Work Type',
        frequency: 'Frequency',
        status: 'Status',
        assignedTo: 'Assigned To',
      },
      empty: 'No maintenance jobs yet',
      emptyPeriod: 'Nothing is due in this period',
      allPeriods: 'Every overdue or postponed job is listed, whatever the period.',
    },
    upcoming: {
      title: 'Upcoming Tasks',
      window: '(Next 7 Days)',
      columns: {
        date: 'Date',
        component: 'Component',
        task: 'Task',
        priority: 'Priority',
      },
      empty: 'No tasks in the next 7 days',
    },
    workOrders: {
      title: 'Work Order',
      search: 'Search work order...',
      searchLabel: 'Search work orders',
      add: 'New Work Order',
      generate: 'Generate Work Orders',
      soon: 'Work orders arrive with the maintenance plan.',
      columns: {
        no: 'WO No',
        component: 'Component',
        task: 'Task',
        priority: 'Priority',
        status: 'Status',
        dueDate: 'Due Date',
        assignedTo: 'Assigned To',
      },
      empty: 'No work orders yet',
      noMatch: 'No work orders match the search',
    },
    calendar: {
      title: 'PMS Calendar',
      previous: 'Previous month',
      next: 'Next month',
      legend: {
        scheduled: 'Scheduled',
        inProgress: 'In Progress',
        overdue: 'Overdue',
        completed: 'Completed',
      },
      today: 'Today, {{date}}',
      empty: 'Nothing scheduled this month',
    },
    quickLinks: {
      title: 'Quick Links',
      plan: 'Maintenance Plan',
      createWorkOrder: 'Create Work Order',
      workOrders: 'Work Orders',
      equipmentList: 'Equipment List',
      reports: 'Reports',
    },
  },
  drawer: {
    addTitle: 'Add Equipment',
    editTitle: 'Edit Equipment',
    sections: {
      identity: 'Identification',
      classification: 'Classification',
      counter: 'Running hours',
      technical: 'Technical data',
      notes: 'Notes',
    },
    parent: 'Parent',
    parentNone: 'Top level (group)',
    nodeType: 'Type',
    code: 'Code',
    name: 'Name',
    tag: 'Equipment Tag No.',
    sfiCode: 'SFI Code',
    maker: 'Maker',
    model: 'Model',
    serialNo: 'Serial No',
    installedOn: 'Installed On',
    location: 'Location',
    status: 'Status',
    critical: 'Critical equipment',
    criticalHint: 'Failure may cause hazardous situations (ISM Code 10.3).',
    criticalReason: 'Reason for criticality',
    classRelevant: 'Class item',
    ismRelevant: 'ISM relevant',
    safetyCritical: 'Safety critical',
    outOfPmsManual: 'Out of PMS',
    outOfPmsReason: 'Reason out of PMS',
    bvItemCode: 'BV Item Code',
    surveyPrinciple: 'Survey principle',
    hasCounter: 'Running hour counter',
    maxDailyHours: 'Daily limit (h)',
    maxDailyHoursHint: 'Readings above this average per day need confirmation.',
    noonReportSource: 'Noon Report source',
    noonNone: 'None',
    noonMain: 'Main engine',
    noonAuxiliary: 'Auxiliary engine',
    noonAuxiliaryName: 'Auxiliary engine name',
    powerKw: 'Power (kW)',
    rpm: 'RPM',
    fuelType: 'Fuel Type',
    cylinders: 'Cylinders',
    coolingType: 'Cooling',
    lubricationType: 'Lubrication',
    startingSystem: 'Starting system',
    notes: 'Notes',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Equipment saved.',
    close: 'Close',
  },
  decommission: {
    title: 'Remove {{name}} from the register',
    intro: 'The item stays in the history with its readings and records; it is no longer planned.',
    reason: 'Reason',
    confirm: 'Remove',
    cancel: 'Cancel',
    close: 'Close',
  },
  particulars: {
    title: 'Vessel particulars',
    docTemplate: 'Document template',
    docTemplateValue: '{{name}} v{{version}}',
    docTemplateNone: 'Not applied yet',
    docTemplateHint:
      'Chosen when the vessel was added; the folder tree came from it. Only Felixmate can change it.',
    vesselType: 'Vessel Type',
    builtYear: 'Built',
    flag: 'Flag',
    classSociety: 'Class',
    classNo: 'Class No',
    callSign: 'Call Sign',
    grossTonnage: 'Gross Tonnage (GT)',
    ismCompany: 'ISM Company (DoC Holder)',
    raGroup: 'Risk Assessment Group',
    raGroupHint: 'Library activities for this vessel type are suggested on board.',
    ownerReference: "Owner's Reference",
    ownerReferenceHint:
      "Speed and consumption used by the analysis when a voyage has no charter party (spot, owner's voyage, yacht). A voyage may override it.",
    ownerSpeedLadenKn: 'Speed Laden',
    ownerSpeedBallastKn: 'Speed Ballast',
    ownerConsumptionLadenMt: 'Consumption Laden',
    ownerConsumptionBallastMt: 'Consumption Ballast',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    readOnly: 'Vessel particulars are maintained by the office.',
    error: 'Vessel particulars could not be loaded.',
  },
  counter: {
    title: 'Enter Running Hours',
    subtitle: '{{name}} · limit {{limit}} h/day',
    tabs: {
      reading: 'Reading',
      replace: 'Replace meter',
    },
    mode: 'Entry',
    modeTotal: 'Meter value',
    modeDelta: 'Since last reading',
    totalHours: 'Meter value (h)',
    deltaHours: 'Hours since last reading',
    readAt: 'Read at (ship time)',
    previous: 'Previous reading',
    previousValue: '{{hours}} on {{date}}',
    previousNone: 'No previous reading',
    result: 'New meter value: {{hours}}',
    suggestion: 'Use Noon Report ({{hours}})',
    suggestionHint: 'Running hours reported on {{date}}.',
    noSuggestion: 'No newer Noon Report value',
    eventKind: 'Event',
    previousMeterFinal: 'Old meter final value (h)',
    meterStartHours: 'New meter start value (h)',
    reason: 'Reason',
    save: 'Save Reading',
    cancel: 'Cancel',
    saved: 'Running hours recorded.',
    confirmTitle: 'Confirm the reading',
    confirmIntro: 'Tick each statement to confirm it is correct.',
    confirmSave: 'Confirm and save',
    back: 'Back',
    close: 'Close',
  },
};

export const pmsTr: typeof pmsEn = {
  errors: { ...errorsTr, ...pmsWorkErrorsTr, ...pmsConditionErrorsTr },
  fieldErrors: { ...fieldErrorsTr, ...pmsWorkFieldErrorsTr },
  setupChanges: {
    title: 'Kurulum aşaması',
    text: 'Bu gemiyi ofis kuruyor. Değişiklikler gemiye gönderilir ve geminin sonraki senkronunda uygulanır; buradaki kayıt ondan sonra güncellenir.',
    titleLive: 'Ofisten değişiklik',
    textLive:
      'Gemi kullanımda. Değişiklikler gemiye gönderilir ve geminin sonraki senkronunda uygulanır; buradaki kayıt ondan sonra güncellenir. Arada gemide değişen kayıt ezilmez: değişiklik uygulanamadı olarak listelenir.',
    pending_one: 'Gemiyi bekleyen {{count}} değişiklik',
    pending_other: 'Gemiyi bekleyen {{count}} değişiklik',
    rejected_one: 'Gemide uygulanamayan {{count}} değişiklik',
    rejected_other: 'Gemide uygulanamayan {{count}} değişiklik',
  },
  rotables: {
    tab: 'Dolaşan parçalar',
    emptyHere: 'Burada takılı dolaşan bileşen yok',
    installedHere: 'Burada takılı dolaşan bileşenler',
    installedSince: '{{day}} tarihinden beri takılı',
    totalHours: 'Kendi saati {{hours}}',
    initialHours: 'kayıtta {{hours}}',
    incomplete: 'eksik (sayaçsız konum)',
    history: 'Geçmiş',
    periods: 'Takılı kaldığı dönemler',
    now: 'şimdi',
    install: 'Dolaşan parça tak',
    remove: 'Sök',
    register: 'Dolaşan parça kaydet',
    movementsHere: 'Bu konumdaki hareketler',
    kinds: { install: 'Takıldı', remove: 'Söküldü' },
    part: 'Dolaşan bileşen',
    movedOn: 'Tarih',
    positionHours: 'Konum çalışma saati',
    positionHoursHint: 'Bu konumun o anki sayaç toplamı; sayacı yoksa boş bırakın.',
    reason: 'Gerekçe / not',
    serialNo: 'Seri No.',
    name: 'Ad',
    partNo: 'Parça No.',
    maker: 'Üretici',
    notes: 'Not',
    initialHoursLabel: 'Kayıttaki saat',
    initialHoursHint: 'Önceden çalıştığı saat (ör. son revizyondan beri); yeniyse 0.',
    registeredOn: 'Kayıt tarihi',
  },
  work: pmsWorkTr,
  postponements: pmsPostponementsTr,
  setupChangeList: pmsSetupChangesTr,
  condition: pmsConditionTr,
  confirmations: {
    LOWER_THAN_PREVIOUS:
      'Değer önceki okumadan ({{previous}} s) düşük. Sayaç değiştirilmedi ya da sıfırlanmadı.',
    DAILY_AVERAGE_OVER_24H:
      'Önceki okumadan bu yana öğe günde 24 saatten fazla çalışmış ({{perDay}} s/gün).',
    DAILY_AVERAGE_OVER_LIMIT:
      'Önceki okumadan bu yana öğe günlük {{limit}} s sınırını aşmış ({{perDay}} s/gün).',
  },
  meterChangeConfirmations: {
    LOWER_THAN_PREVIOUS:
      'Eski sayacın son değeri ({{value}} s) önceki okumadan ({{previous}} s) düşük.',
    DAILY_AVERAGE_OVER_24H:
      'Eski sayacın son değerine ({{value}} s) göre öğe önceki okumadan bu yana günde 24 saatten fazla çalışmış ({{perDay}} s/gün).',
    DAILY_AVERAGE_OVER_LIMIT:
      'Eski sayacın son değerine ({{value}} s) göre öğe önceki okumadan bu yana günlük {{limit}} s sınırını aşmış ({{perDay}} s/gün).',
  },
  nodeTypes: {
    group: 'Grup',
    system: 'Sistem',
    equipment: 'Ekipman',
    component: 'Bileşen',
  },
  statuses: {
    operational: 'Çalışıyor',
    operational_with_restrictions: 'Kısıtlı',
    not_operational: 'Çalışmıyor',
  },
  surveyPrinciples: {
    pms_ce: 'PMS (Başmühendis)',
    surveyor: 'Sörveyör',
    condition_monitoring: 'Durum izleme',
    none: 'Yok',
  },
  readingKinds: {
    reading: 'Okuma',
    meter_replaced: 'Sayaç değişti',
    meter_reset: 'Sayaç sıfırlandı',
  },
  readingSources: {
    manual: 'Elle',
    noon_report: 'Noon Report',
  },
  hours: '{{value}} s',
  equipment: {
    title: 'Ekipman Listesi',
    loading: 'Ekipman yükleniyor…',
    error: 'Ekipman kaydı yüklenemedi.',
    retry: 'Yeniden dene',
    noVessel: 'Size atanmış gemi yok.',
    tableOf: '{{name}} tablosu',
    card: {
      label: 'Gemi',
      vessel: 'Gemi',
      imo: 'IMO {{imo}}',
      vesselType: 'Gemi Tipi',
      built: 'İnşa',
      flag: 'Bayrak',
      nextPort: 'Sonraki Liman',
      search: 'Ekipman, bileşen ara...',
      searchLabel: 'Ekipman ara',
      add: 'Ekipman Ekle',
      export: 'Dışa Aktar',
      exportHint: 'Dışa aktarma PMS raporlarıyla gelir.',
      noResults: 'Ekipman bulunamadı',
    },
    tree: {
      title: 'Ekipman Ağacı',
      label: 'Ekipman ağacı',
      search: 'Ara...',
      searchLabel: 'Ekipman ağacında ara',
      open: 'Ekipman Ağacı',
      empty: 'Henüz ekipman yok',
      emptyShip: 'İlk öğeyi ekleyin ya da varsayılan yapıyı yükleyin.',
      emptyWeb: 'Gemi henüz ekipman kaydetmedi.',
      emptyQueued:
        "Kaydı ofisten kurun: ekipman ekleyin, Component Library'den uygulayın ya da varsayılan yapıyı yükleyin. Gemi değişiklikleri sonraki senkronda uygular.",
      emptyCloud: 'Gemi etkinleşmeden önce kaydı hazırlayın.',
      loadDefault: 'Varsayılan yapıyı yükle',
      noMatch: '"{{query}}" ile eşleşen ekipman yok.',
      critical: 'Kritik',
      counter: 'Çalışma saati sayacı',
    },
    overview: {
      title: '{{name}} – Ekipman Özeti',
      titleNone: 'Ekipman Özeti',
      totalComponents: 'Toplam Bileşen',
      pmDue: 'PM Görevleri (Vadesi Gelen)',
      overdue: 'Gecikmiş',
      completed: 'Tamamlanan (30 Gün)',
      columns: {
        component: 'Bileşen',
        type: 'Tür',
        workType: 'İş Türü',
        frequency: 'Sıklık',
        lastDone: 'Son Yapılan',
        nextDue: 'Sonraki Vade',
        status: 'Durum',
        actions: 'Eylemler',
      },
      empty: 'Henüz bakım işi yok',
      emptyHint: 'Bu öğenin işleri Bakım Planı’nda tanımlanır.',
      selectHint: 'Ağaçtan bir öğe seçin.',
    },
    details: {
      title: 'Bileşen Ayrıntıları',
      tabs: {
        general: 'Genel',
        plan: 'Bakım Planı',
        planShort: 'Plan',
        history: 'Geçmiş',
        documents: 'Belgeler',
        rotables: 'Dolaşan parçalar',
      },
      equipment: 'Ekipman',
      component: 'Bileşen',
      workType: 'İş Türü',
      frequency: 'Sıklık',
      responsible: 'Sorumlu',
      location: 'Konum',
      tagShort: 'Tag',
      notes: 'Notlar',
      runningHours: 'Çalışma Saati',
      lastReading: 'Son Okuma',
      lastReadingValue: '{{date}} tarihinde {{hours}}',
      code: 'Kod',
      maker: 'Üretici / Model',
      serialNo: 'Seri No',
      critical: 'Kritik',
      criticalInherited: '{{name}} üzerinden kritik',
      classRelevant: 'Klas',
      edit: 'Düzenle',
      viewManual: 'Kılavuzu Aç',
      viewManualHint: 'Bu öğeye henüz bağlı kılavuz yok.',
      enterHours: 'Çalışma Saati Gir',
      decommission: 'Kayıttan kaldır',
      editedOnBoard: 'Gemide düzenlenir',
      editedOnBoardHint: 'Gemi etkin: ekipman kaydı gemide tutulur.',
      preparedInOffice: 'Ofiste hazırlandı',
      noSelection: 'Ayrıntıları görmek için bir öğe seçin.',
      planEmpty: 'Henüz bakım işi yok',
      nextDue: 'Sıradaki vade {{date}}',
      workRecords: 'İş kayıtları',
      instructionNo: 'Talimat No {{no}}',
      historyEmpty: 'Henüz çalışma saati okuması yok',
      auditTrail: 'Değişiklik geçmişi (denetim kaydı)',
      historyColumns: {
        date: 'Tarih',
        kind: 'Tür',
        hours: 'Sayaç',
        cumulative: 'Birikmiş',
        by: 'Kaydeden',
      },
      historyShowing: '{{total}} okumadan {{from}}–{{to}} arası',
      historyPages: 'Geçmiş sayfaları',
      historySort: 'Tarihe göre sırala',
      historyPage: {
        first: 'İlk sayfa',
        previous: 'Önceki sayfa',
        next: 'Sonraki sayfa',
        last: 'Son sayfa',
      },
      documentsEmpty: 'Henüz kılavuz, talimat ya da ek yok',
      photo: '{{type}} simgesi',
    },
    plan: {
      title: 'Bakım Planı',
      month: 'Ay',
      week: 'Hafta',
      list: 'Liste',
      today: 'Bugün',
      previous: 'Önceki ay',
      next: 'Sonraki ay',
      previousWeek: 'Önceki hafta',
      nextWeek: 'Sonraki hafta',
      period: 'Dönem',
      columns: {
        date: 'Tarih',
        component: 'Bileşen',
        workType: 'İş Türü',
        frequency: 'Sıklık',
        status: 'Durum',
        assignedTo: 'Atanan',
      },
      empty: 'Henüz bakım işi yok',
      emptyPeriod: 'Bu dönemde vadesi gelen iş yok',
      allPeriods: 'Geciken ve ertelenen işlerin hepsi listelenir; dönem sınırlamaz.',
    },
    upcoming: {
      title: 'Yaklaşan Görevler',
      window: '(Sonraki 7 Gün)',
      columns: {
        date: 'Tarih',
        component: 'Bileşen',
        task: 'Görev',
        priority: 'Öncelik',
      },
      empty: 'Sonraki 7 günde görev yok',
    },
    workOrders: {
      title: 'İş Emri',
      search: 'İş emri ara...',
      searchLabel: 'İş emirlerinde ara',
      add: 'Yeni İş Emri',
      generate: 'İş Emirlerini Oluştur',
      soon: 'İş emirleri bakım planıyla gelir.',
      columns: {
        no: 'İE No',
        component: 'Bileşen',
        task: 'Görev',
        priority: 'Öncelik',
        status: 'Durum',
        dueDate: 'Vade',
        assignedTo: 'Atanan',
      },
      empty: 'Henüz iş emri yok',
      noMatch: 'Aramaya uyan iş emri yok',
    },
    calendar: {
      title: 'PMS Takvimi',
      previous: 'Önceki ay',
      next: 'Sonraki ay',
      legend: {
        scheduled: 'Planlandı',
        inProgress: 'Sürüyor',
        overdue: 'Gecikmiş',
        completed: 'Tamamlandı',
      },
      today: 'Bugün, {{date}}',
      empty: 'Bu ay planlanmış iş yok',
    },
    quickLinks: {
      title: 'Hızlı Bağlantılar',
      plan: 'Bakım Planı',
      createWorkOrder: 'İş Emri Oluştur',
      workOrders: 'İş Emirleri',
      equipmentList: 'Ekipman Listesi',
      reports: 'Raporlar',
    },
  },
  drawer: {
    addTitle: 'Ekipman Ekle',
    editTitle: 'Ekipmanı Düzenle',
    sections: {
      identity: 'Kimlik',
      classification: 'Sınıflandırma',
      counter: 'Çalışma saati',
      technical: 'Teknik bilgiler',
      notes: 'Notlar',
    },
    parent: 'Üst öğe',
    parentNone: 'En üst düzey (grup)',
    nodeType: 'Tür',
    code: 'Kod',
    name: 'Ad',
    tag: 'Ekipman Etiket No.',
    sfiCode: 'SFI Kodu',
    maker: 'Üretici',
    model: 'Model',
    serialNo: 'Seri No',
    installedOn: 'Kurulum Tarihi',
    location: 'Konum',
    status: 'Durum',
    critical: 'Kritik ekipman',
    criticalHint: 'Arızası tehlikeli durumlara yol açabilir (ISM Kod 10.3).',
    criticalReason: 'Kritiklik gerekçesi',
    classRelevant: 'Klas kalemi',
    ismRelevant: 'ISM ile ilgili',
    safetyCritical: 'Emniyet açısından kritik',
    outOfPmsManual: 'PMS dışında',
    outOfPmsReason: 'PMS dışı gerekçesi',
    bvItemCode: 'BV Kalem Kodu',
    surveyPrinciple: 'Sörvey ilkesi',
    hasCounter: 'Çalışma saati sayacı',
    maxDailyHours: 'Günlük sınır (s)',
    maxDailyHoursHint: 'Günlük ortalaması bunu aşan okumalar onay ister.',
    noonReportSource: 'Noon Report kaynağı',
    noonNone: 'Yok',
    noonMain: 'Ana makine',
    noonAuxiliary: 'Yardımcı makine',
    noonAuxiliaryName: 'Yardımcı makine adı',
    powerKw: 'Güç (kW)',
    rpm: 'Devir (rpm)',
    fuelType: 'Yakıt Türü',
    cylinders: 'Silindir',
    coolingType: 'Soğutma',
    lubricationType: 'Yağlama',
    startingSystem: 'Yol verme sistemi',
    notes: 'Notlar',
    save: 'Kaydet',
    cancel: 'Vazgeç',
    saved: 'Ekipman kaydedildi.',
    close: 'Kapat',
  },
  decommission: {
    title: '{{name}} kayıttan kaldırılsın',
    intro: 'Öğe okuma ve kayıtlarıyla geçmişte kalır; artık planlanmaz.',
    reason: 'Gerekçe',
    confirm: 'Kaldır',
    cancel: 'Vazgeç',
    close: 'Kapat',
  },
  particulars: {
    title: 'Gemi bilgileri',
    docTemplate: 'Doküman şablonu',
    docTemplateValue: '{{name}} v{{version}}',
    docTemplateNone: 'Henüz uygulanmadı',
    docTemplateHint:
      'Gemi eklenirken seçildi; klasör ağacı ondan geldi. Yalnız Felixmate değiştirebilir.',
    vesselType: 'Gemi Tipi',
    builtYear: 'İnşa Yılı',
    flag: 'Bayrak',
    classSociety: 'Klas',
    classNo: 'Klas No',
    callSign: 'Çağrı İşareti',
    grossTonnage: 'Gros Tonaj (GT)',
    ismCompany: 'ISM Şirketi (DoC Sahibi)',
    raGroup: 'Risk Değerlendirme Grubu',
    raGroupHint: 'Gemide bu gemi tipinin kütüphane faaliyetleri önerilir.',
    ownerReference: 'Armatör Referansı',
    ownerReferenceHint:
      'Çarter sözleşmesi olmayan seferde (spot, armatör seferi, yat) analizin kullandığı hız ve tüketim. Sefer kendi değeriyle değiştirebilir.',
    ownerSpeedLadenKn: 'Hız (Yüklü)',
    ownerSpeedBallastKn: 'Hız (Balastlı)',
    ownerConsumptionLadenMt: 'Tüketim (Yüklü)',
    ownerConsumptionBallastMt: 'Tüketim (Balastlı)',
    save: 'Kaydet',
    cancel: 'Vazgeç',
    close: 'Kapat',
    readOnly: 'Gemi bilgilerini ofis yönetir.',
    error: 'Gemi bilgileri okunamadı.',
  },
  counter: {
    title: 'Çalışma Saati Gir',
    subtitle: '{{name}} · sınır {{limit}} s/gün',
    tabs: {
      reading: 'Okuma',
      replace: 'Sayaç değişimi',
    },
    mode: 'Giriş',
    modeTotal: 'Sayaç değeri',
    modeDelta: 'Son okumadan bu yana',
    totalHours: 'Sayaç değeri (s)',
    deltaHours: 'Son okumadan bu yana saat',
    readAt: 'Okuma zamanı (gemi saati)',
    previous: 'Önceki okuma',
    previousValue: '{{date}} tarihinde {{hours}}',
    previousNone: 'Önceki okuma yok',
    result: 'Yeni sayaç değeri: {{hours}}',
    suggestion: 'Noon Report değerini kullan ({{hours}})',
    suggestionHint: '{{date}} tarihinde bildirilen çalışma saati.',
    noSuggestion: 'Daha yeni Noon Report değeri yok',
    eventKind: 'Olay',
    previousMeterFinal: 'Eski sayacın son değeri (s)',
    meterStartHours: 'Yeni sayacın başlangıcı (s)',
    reason: 'Gerekçe',
    save: 'Okumayı Kaydet',
    cancel: 'Vazgeç',
    saved: 'Çalışma saati kaydedildi.',
    confirmTitle: 'Okumayı onaylayın',
    confirmIntro: 'Doğru olduğunu onaylamak için her ifadeyi işaretleyin.',
    confirmSave: 'Onayla ve kaydet',
    back: 'Geri',
    close: 'Kapat',
  },
};
